import { EntityAdapter, createEntityAdapter } from "@ngrx/entity";
import { Action, createReducer, on } from "@ngrx/store";
import * as appointmentsActions from "./calendar.actions";
import { AppointmentDto } from "../interfaces/appointment_dto";

export const calendarFeatureKey = "appointments";

export interface State {
    [calendarFeatureKey]: AppointmentsState;
    calendarView: string;
    isLoading: boolean;
}

export interface AppointmentsState extends EntityAdapter<AppointmentDto> {
    selectedLesson: number | null;
    calendarView: string;
    isLoading: boolean;
}

export const adapter = createEntityAdapter<AppointmentDto>({
    selectId: (appointment: AppointmentDto) => String(appointment.Id) || "",
    sortComparer: false,
});

export const initialState = adapter.getInitialState({
    selectedLesson: null,
    calendarView: "Week",
    isLoading: false,
});

const appointmentsReducer = createReducer(
    initialState,
    on(appointmentsActions.changeCalendarView,
        (state, { view }) => ({ ...state, calendarView: view }),
    ),
    on(appointmentsActions.loadAppointments,
        (state) => ({ ...state, isLoading: true }),
    ),
    on(appointmentsActions.loadAppointmentsSuccess,
        (state, { appointments }) => adapter.setAll(appointments, state),
    ),
    on(appointmentsActions.loadAppointmentsFailure,
        (state) => ({ ...state, isLoading: false }),
    ),
    on(appointmentsActions.loadAppointmentsSuccess,
        (state) => ({ ...state, isLoading: false }),
    ),
    on(appointmentsActions.loadUpcomingLessons,
        (state) => ({ ...state, isLoading: true }),
    ),
    on(appointmentsActions.loadUpcomingLessonsSuccess,
        (state, { lessons }) => {
            // First, filter out all future appointments from the current state
            const currentDate = new Date();
            currentDate.setHours(0, 0, 0, 0);
            const futureAppointments = state.ids
                .map(id => state.entities[id])
                .filter(appointment => {
                    return (appointment.StartTime >= currentDate  && appointment.Lessons);
                });
            
            // Create a new state without the future appointments that are not in the lessons array
            const deletedFutureAppointments = futureAppointments.filter(appointment => !lessons.some(lesson => lesson.Id === appointment.Id));

            const updatedState = adapter.removeMany(deletedFutureAppointments.map(appointment => String(appointment.Id)), state);
            const newState = adapter.upsertMany(lessons, updatedState);
            return { ...newState, isLoading: false };
        },
    ),
    on(appointmentsActions.addLesson,
        (state) => ({ ...state, isLoading: true }),
    ),
    on(appointmentsActions.addLessonSuccess,
        (state, { lesson }) => {
            const newState = adapter.addOne(lesson, state);
            return { ...newState, isLoading: false };
        },
    ),
    on(appointmentsActions.deleteAppointmentSuccess, (state, { appointment }) =>  {
        if (appointment === null || appointment === undefined) return state;
        const newState = adapter.removeOne(String(appointment.Id), state);

        if (state.selectedLesson && state.selectedLesson === appointment.Id) {
            return { ...newState, selectedLesson: newState.ids[0] };
        }

        return newState;
    }),
    on(appointmentsActions.updateLesson,
        (state) => ({ ...state, isLoading: true }),
    ),
    on(appointmentsActions.updateLessonSuccess,
        (state, { lesson }) => {
            const updatedLesson = { ...lesson };
            const newState = adapter.updateOne({ id: String(updatedLesson.Id), changes: updatedLesson }, state);
            return { ...newState, isLoading: false };
        }
    ),
    on(appointmentsActions.selectLesson,
        (state, { lesson }) => ({ ...state, selectedLesson: lesson?.Id }),
    ),
    on(appointmentsActions.upsertAppointments,
        (state, { appointments }) => adapter.upsertMany(appointments, state),
    ),
    on(appointmentsActions.getGoogleEventsSuccess,
        (state, { events }) => adapter.upsertMany(events, state),
    ),
    on(appointmentsActions.getLocalEventsSuccess,
        (state, { events }) => adapter.upsertMany(events, state),
    ),
    on(appointmentsActions.sendAppointmentReminderSuccess,
        (state, {sendAppointmentRequest}) => adapter.updateOne({ id: String(sendAppointmentRequest.appointmentId), changes: {MailId: "sent"} }, state),
    )
);

export function calendarReducer(
    state: any,
    action: Action,
) {
    return appointmentsReducer(state, action);
}
